export interface FramingMesh { pos: ArrayLike<number>; joints: ArrayLike<number>; weights: ArrayLike<number>; mask?:Uint8Array }
/** Conservative skinning envelope: every weighted point is a convex combination
 * of transformed points inside these bone-local boxes. Eight corners per used
 * bone replace a full dressed-mesh scan in gameplay frames. */
export function createBoundsProxy(meshes:FramingMesh[]):FramingMesh {
  const boxes=new Map<number,{min:number[];max:number[]}>();
  for(const mesh of meshes)for(let i=0;i<mesh.pos.length/3;i++) {
    if(mesh.mask&&!mesh.mask[i])continue;
    for(let k=0;k<4;k++) {
      if(mesh.weights[i*4+k]<=0)continue;
      const bone=mesh.joints[i*4+k];let box=boxes.get(bone);
      if(!box){box={min:[Infinity,Infinity,Infinity],max:[-Infinity,-Infinity,-Infinity]};boxes.set(bone,box);}
      for(let axis=0;axis<3;axis++){const p=mesh.pos[i*3+axis];box.min[axis]=Math.min(box.min[axis],p);box.max[axis]=Math.max(box.max[axis],p);}
    }
  }
  const pos=new Float32Array(boxes.size*24),joints=new Uint8Array(boxes.size*32),weights=new Float32Array(boxes.size*32);
  let index=0;
  for(const [bone,box] of boxes)for(let corner=0;corner<8;corner++) {
    for(let axis=0;axis<3;axis++)pos[index*3+axis]=(corner&(1<<axis)?box.max:box.min)[axis];
    joints[index*4]=bone;weights[index*4]=1;index++;
  }
  return {pos,joints,weights};
}
/** Fit the actual dressed, posed geometry. Ears and grafted heads are part of the figure. */
export function posedBounds(meshes: FramingMesh[], palette: Float32Array) {
  let minY = Infinity, maxY = -Infinity, radius = 0;
  for (const mesh of meshes) for (let i = 0; i < mesh.pos.length / 3; i++) {
    if (mesh.mask && !mesh.mask[i]) continue;
    const x = mesh.pos[i*3], y = mesh.pos[i*3+1], z = mesh.pos[i*3+2];
    let px=0,py=0,pz=0;
    for (let k=0;k<4;k++) {
      const w=mesh.weights[i*4+k]; if (!w) continue;
      const o=mesh.joints[i*4+k]*16;
      px+=(palette[o]*x+palette[o+4]*y+palette[o+8]*z+palette[o+12])*w;
      py+=(palette[o+1]*x+palette[o+5]*y+palette[o+9]*z+palette[o+13])*w;
      pz+=(palette[o+2]*x+palette[o+6]*y+palette[o+10]*z+palette[o+14])*w;
    }
    minY=Math.min(minY,py);maxY=Math.max(maxY,py);radius=Math.max(radius,Math.hypot(px,pz));
  }
  return Number.isFinite(minY) ? {minY,maxY,radius} : {minY:0,maxY:1,radius:.45};
}
